import { createAppRequest } from '@/services/request';
import type {
  AddEntityTagPayload,
  BatchCreateFromDiscoveryPayload,
  BatchCreateFromDiscoveryResult,
  CreateTagPayload,
  CreateEntityTypePayload,
  CreateEntityExtensionPayload,
  CreateEntityPayload,
  EntityMappingQuery,
  EntityMergeLogQuery,
  CreateRelationPayload,
  CreateRelationTypePayload,
  DiscoverEntitiesPayload,
  DiscoverEntitiesResult,
  EntityExtensionQuery,
  EntityQuery,
  EntityPropertiesValidationResult,
  EntitySchemaValidationResult,
  EntityTypeQuery,
  ImportTaskQuery,
  GenerateEntityTypeFieldsPayload,
  GenerateEntityTypeFieldsResult,
  GenerateRelationTypeCodePayload,
  GenerateRelationTypeCodeResult,
  InitializeEntityTypesResult,
  InitializeRelationTypesResult,
  KnowledgeGraphEntity,
  KnowledgeGraphEntityExtension,
  KnowledgeGraphEntityMapping,
  KnowledgeGraphEntityMergeLog,
  KnowledgeGraphTag,
  KnowledgeGraphEntityType,
  KnowledgeGraphImportTask,
  KnowledgeGraphRelation,
  KnowledgeGraphRelationType,
  KnowledgeGraphRequestOptions,
  KnowledgeGraphSettings,
  PagedResult,
  RelationPropertiesValidationResult,
  RelationSchemaValidationResult,
  RelationQuery,
  RelationTypeQuery,
  SaveKnowledgeGraphSettingsPayload,
  UpdateEntityTypePayload,
  UpdateEntityExtensionPayload,
  UpdateEntityPayload,
  UpdateRelationPayload,
  UpdateRelationTypePayload,
  ValidateEntityTypePropertiesPayload,
  ValidateEntityTypeSchemaPayload,
  ValidateRelationTypePropertiesPayload,
  ValidateRelationTypeSchemaPayload,
} from './types/knowledgeGraph';
import { normalizePageParams } from './pagination';

const request = createAppRequest();

export async function queryEntities(
  params: EntityQuery,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<PagedResult<KnowledgeGraphEntity>>('/bff/zy-knowledge-graph/entities', {
    method: 'GET',
    params: normalizePageParams(params),
    ...(options || {}),
  });
}

export async function getEntity(
  uuid: string,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<KnowledgeGraphEntity>(`/bff/zy-knowledge-graph/entities/${uuid}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getEntityTags(
  uuid: string,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<KnowledgeGraphTag[]>(`/bff/zy-knowledge-graph/entities/${uuid}/tags`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createTag(
  data: CreateTagPayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<KnowledgeGraphEntity>('/bff/zy-knowledge-graph/entities/tags', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });
}

export async function addTagToEntity(
  uuid: string,
  data: AddEntityTagPayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request(`/bff/zy-knowledge-graph/entities/${uuid}/tags`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });
}

export async function removeTagFromEntity(
  uuid: string,
  tagEntityUuid: string,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<{ success: boolean }>(
    `/bff/zy-knowledge-graph/entities/${uuid}/tags/${tagEntityUuid}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
}

export async function createEntity(
  data: CreateEntityPayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<KnowledgeGraphEntity>('/bff/zy-knowledge-graph/entities', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });
}

export async function updateEntity(
  uuid: string,
  data: UpdateEntityPayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<KnowledgeGraphEntity>(`/bff/zy-knowledge-graph/entities/${uuid}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });
}

export async function deleteEntity(
  uuid: string,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<{ success: boolean }>(`/bff/zy-knowledge-graph/entities/${uuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function queryEntityExtensions(
  params: EntityExtensionQuery,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<PagedResult<KnowledgeGraphEntityExtension>>(
    '/bff/zy-knowledge-graph/entity-extensions',
    {
      method: 'GET',
      params: normalizePageParams(params),
      ...(options || {}),
    },
  );
}

export async function queryImportTasks(
  params: ImportTaskQuery,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<PagedResult<KnowledgeGraphImportTask>>('/bff/zy-knowledge-graph/import-tasks', {
    method: 'GET',
    params: normalizePageParams(params),
    ...(options || {}),
  });
}

export async function queryEntityMappings(
  params: EntityMappingQuery,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<PagedResult<KnowledgeGraphEntityMapping>>(
    '/bff/zy-knowledge-graph/entity-mappings',
    {
      method: 'GET',
      params: normalizePageParams(params),
      ...(options || {}),
    },
  );
}

export async function queryEntityMergeLogs(
  params: EntityMergeLogQuery,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<PagedResult<KnowledgeGraphEntityMergeLog>>(
    '/bff/zy-knowledge-graph/entity-merge-logs',
    {
      method: 'GET',
      params: normalizePageParams(params),
      ...(options || {}),
    },
  );
}

export async function createEntityExtension(
  data: CreateEntityExtensionPayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<KnowledgeGraphEntityExtension>('/bff/zy-knowledge-graph/entity-extensions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });
}

export async function updateEntityExtension(
  uuid: string,
  data: UpdateEntityExtensionPayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<KnowledgeGraphEntityExtension>(
    `/bff/zy-knowledge-graph/entity-extensions/${uuid}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
      ...(options || {}),
    },
  );
}

export async function queryEntityTypes(
  params: EntityTypeQuery,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<PagedResult<KnowledgeGraphEntityType>>('/bff/zy-knowledge-graph/entity-types', {
    method: 'GET',
    params: normalizePageParams(params),
    ...(options || {}),
  });
}

export async function getEntityType(
  uuid: string,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<KnowledgeGraphEntityType>(`/bff/zy-knowledge-graph/entity-types/${uuid}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createEntityType(
  data: CreateEntityTypePayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<KnowledgeGraphEntityType>('/bff/zy-knowledge-graph/entity-types', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });
}

export async function updateEntityType(
  uuid: string,
  data: UpdateEntityTypePayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<KnowledgeGraphEntityType>(`/bff/zy-knowledge-graph/entity-types/${uuid}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });
}

export async function deleteEntityType(
  uuid: string,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<{ success: boolean }>(`/bff/zy-knowledge-graph/entity-types/${uuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function initializeEntityTypes(options?: KnowledgeGraphRequestOptions) {
  return request<InitializeEntityTypesResult>('/bff/zy-knowledge-graph/entity-types/initialize', {
    method: 'POST',
    ...(options || {}),
  });
}

export async function generateEntityTypeFields(
  data: GenerateEntityTypeFieldsPayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<GenerateEntityTypeFieldsResult>(
    '/bff/zy-knowledge-graph/entity-types/generate-fields',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
      ...(options || {}),
    },
  );
}

export async function validateEntityTypeSchema(
  data: ValidateEntityTypeSchemaPayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<EntitySchemaValidationResult>('/bff/zy-knowledge-graph/entity-types/validate-schema', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });
}

export async function validateEntityTypeProperties(
  data: ValidateEntityTypePropertiesPayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<EntityPropertiesValidationResult>(
    '/bff/zy-knowledge-graph/entity-types/validate-properties',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
      ...(options || {}),
    },
  );
}

export async function getKnowledgeGraphSettings(options?: KnowledgeGraphRequestOptions) {
  return request<KnowledgeGraphSettings>('/bff/zy-knowledge-graph/settings', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function updateKnowledgeGraphSettings(
  data: SaveKnowledgeGraphSettingsPayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<KnowledgeGraphSettings>('/bff/zy-knowledge-graph/settings', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });
}

export async function resetKnowledgeGraphSettings(options?: KnowledgeGraphRequestOptions) {
  return request<KnowledgeGraphSettings>('/bff/zy-knowledge-graph/settings/reset', {
    method: 'POST',
    ...(options || {}),
  });
}

export async function queryRelationTypes(
  params: RelationTypeQuery,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<PagedResult<KnowledgeGraphRelationType>>('/bff/zy-knowledge-graph/relation-types', {
    method: 'GET',
    params: normalizePageParams(params),
    ...(options || {}),
  });
}

export async function getRelationType(
  uuid: string,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<KnowledgeGraphRelationType>(`/bff/zy-knowledge-graph/relation-types/${uuid}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createRelationType(
  data: CreateRelationTypePayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<KnowledgeGraphRelationType>('/bff/zy-knowledge-graph/relation-types', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });
}

export async function updateRelationType(
  uuid: string,
  data: UpdateRelationTypePayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<KnowledgeGraphRelationType>(`/bff/zy-knowledge-graph/relation-types/${uuid}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });
}

export async function deleteRelationType(
  uuid: string,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<{ success: boolean }>(`/bff/zy-knowledge-graph/relation-types/${uuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function generateRelationTypeCode(
  data: GenerateRelationTypeCodePayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<GenerateRelationTypeCodeResult>(
    '/bff/zy-knowledge-graph/relation-types/generate-code',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
      ...(options || {}),
    },
  );
}

export async function initializeRelationTypes(options?: KnowledgeGraphRequestOptions) {
  return request<InitializeRelationTypesResult>(
    '/bff/zy-knowledge-graph/relation-types/initialize',
    {
      method: 'POST',
      ...(options || {}),
    },
  );
}

export async function validateRelationTypeSchema(
  data: ValidateRelationTypeSchemaPayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<RelationSchemaValidationResult>(
    '/bff/zy-knowledge-graph/relation-types/validate-schema',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
      ...(options || {}),
    },
  );
}

export async function validateRelationTypeProperties(
  data: ValidateRelationTypePropertiesPayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<RelationPropertiesValidationResult>(
    '/bff/zy-knowledge-graph/relation-types/validate-properties',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
      ...(options || {}),
    },
  );
}

export async function queryRelations(
  params: RelationQuery,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<PagedResult<KnowledgeGraphRelation>>('/bff/zy-knowledge-graph/relations', {
    method: 'GET',
    params: normalizePageParams(params),
    ...(options || {}),
  });
}

export async function getRelation(
  uuid: string,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<KnowledgeGraphRelation>(`/bff/zy-knowledge-graph/relations/${uuid}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createRelation(
  data: CreateRelationPayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<KnowledgeGraphRelation>('/bff/zy-knowledge-graph/relations', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });
}

export async function updateRelation(
  uuid: string,
  data: UpdateRelationPayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<KnowledgeGraphRelation>(`/bff/zy-knowledge-graph/relations/${uuid}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });
}

export async function deleteRelation(
  uuid: string,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<{ success: boolean }>(`/bff/zy-knowledge-graph/relations/${uuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function discoverEntities(
  data: DiscoverEntitiesPayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<DiscoverEntitiesResult>('/bff/zy-knowledge-graph/entities/discover', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });
}

export async function batchCreateFromDiscovery(
  data: BatchCreateFromDiscoveryPayload,
  options?: KnowledgeGraphRequestOptions,
) {
  return request<BatchCreateFromDiscoveryResult>(
    '/bff/zy-knowledge-graph/entities/batch-create-from-discovery',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
      ...(options || {}),
    },
  );
}
